import React, {
	useMemo,
	useState,
} from 'react';
import {
	Alert,
	Flex,
	Space,
	Statistic,
} from 'antd';
import { formatCurrency } from '@shared/utils/currency';
import {
	Currencies,
} from '@shared/utils/constants';
import { Values } from '@shared/utils/objectEnums';
import {
	calculateTotal,
	round,
} from '@shared/utils/math';
import type { OutStandingData } from '@api/features/statistics/getOutstandingsData';
import Card from '@client/components/Card/Card';
import { DashboardComponent } from '../helpers/dashboardInfo';
import styles from './OutstandingsDashboard.module.css';
import OutstandingsTableSection from './OutstandingsTableSection';

const getSummaryRows = (stats: Array<{
	currency: Values<typeof Currencies>;
	outstanding: number;
	invoiced: number;
	allocated: number;
	unallocated: number;
}>) => {
	const multipleCurrencies = stats.length > 1;
	const rows = [
		{ label: 'Total invoiced', key: 'invoiced' },
		...(multipleCurrencies ? [] : [{ label: 'Total allocated payments', key: 'allocated' }]),
		...(multipleCurrencies ? [] : [{ label: 'Total unallocated payments', key: 'unallocated' }]),
		{ label: 'Total outstanding', key: 'outstanding' },
	];

	if (stats.length === 0) {
		return null;
	}

	if (stats.length > 1) {
		return stats.map((stat) => (
			<Card
				key={stat.currency}
				size="small"
				title={stat.currency}
				className={styles.card}
			>
				<Space direction="vertical">
					{rows.map(({ label, key }) => (
						<Statistic
							value={stat[key as keyof typeof stat]}
							title={label}
							valueRender={
								() => formatCurrency(stat[key as keyof typeof stat], stat.currency,
									{ forceDecimals: true, maximumFractionDigits: 2 })
							}
						/>
					))}
				</Space>
			</Card>
		));
	}

	const values = stats[0];

	if (values == null) {
		return null;
	}

	return rows.map(({ label, key }) => (
		<Card
			key={`${label}-${key}-${values.currency}`}
			size="small"
			title={`${label}${multipleCurrencies ? ` - ${values.currency}` : ''}`}
			className={styles.card}
		>
			<Statistic
				value={values[key as keyof typeof values]}
				valueRender={
					() => formatCurrency(values[key as keyof typeof values], values.currency,
						{ forceDecimals: true, maximumFractionDigits: 2 })
				}
			/>
		</Card>
	));
};

const calculateStats = (data: Array<OutStandingData> | undefined) => {
	if (data == null) {
		return [];
	}

	const currencyObj: {
		[key in typeof Currencies as string]: {
			outstanding: number;
			invoiced: number;
			unallocated: number;
			allocated: number;
		}
	} = {};

	const filteredContracts = new Set<number>();
	const currencies = new Set<string>();
	data.forEach((row) => {
		filteredContracts.add(row.contractId);
		currencies.add(row.currency);
	});

	const sortedArr = Array.from(filteredContracts).sort((a, b) => a - b);
	currencies.forEach((c) => {
		currencyObj[c] = {
			invoiced: 0,
			outstanding: 0,
			unallocated: 0,
			allocated: 0,
		};
	});

	sortedArr.forEach((contractId) => {
		const relevantRows = data.filter((row) => row.contractId === contractId);
		const { currency } = relevantRows[0];

		const invoiced = calculateTotal(relevantRows, (row) => row.amount);
		const allocated = relevantRows[0].allocatedAmountContract;
		const unallocated = relevantRows[0].unallocatedAmountContract;
		const outstanding = invoiced - Math.abs(allocated) - Math.abs(unallocated);

		currencyObj[currency] = {
			invoiced: currencyObj[currency].invoiced + invoiced,
			outstanding: currencyObj[currency].outstanding + outstanding,
			allocated: currencyObj[currency].allocated + allocated,
			unallocated: currencyObj[currency].unallocated + unallocated,
		};
	});

	return Object.entries(currencyObj).map((c) => {
		const { outstanding, invoiced, unallocated, allocated } = c[1];

		const parsedOutstanding = typeof outstanding === 'number' ? round(outstanding, 2) : 0;
		const parsedInvoiced = typeof invoiced === 'number' ? round(invoiced, 2) : 0;
		const parsedUnallocated = typeof unallocated === 'number' ? round(unallocated, 2) : 0;
		const parsedAllocated = typeof allocated === 'number' ? round(allocated, 2) : 0;

		return {
			currency: c[0] as Values<typeof Currencies>,
			outstanding: parsedOutstanding,
			invoiced: parsedInvoiced,
			unallocated: parsedUnallocated,
			allocated: parsedAllocated,
		};
	});
};

const OutstandingsDashboard: DashboardComponent = ({ setLastUpdated, options }) => {
	const eua = options?.eua ?? false;
	const [tableRows, setTableRows] = useState<OutStandingData[]>([]);
	const totalStats = useMemo(() => calculateStats(tableRows), [tableRows]);

	return (
		<>
			<Space direction="vertical" className={styles.mainContainer}>
				<Space>
					{getSummaryRows(totalStats)}
				</Space>
				<div className={styles.controlContainer}>
					<Flex gap={20} align="center">
						<Alert
							className={styles.alert}
							closable
							message="It's possible to filter on many different parameters, such as vessel, contract & counterparty."
							showIcon
						/>
					</Flex>
				</div>
			</Space>
			<OutstandingsTableSection
				eua={eua}
				title=""
				setLastUpdated={setLastUpdated}
				className={styles.table}
				onFilteredRowsChange={setTableRows}
				disableCurrencyFilter
			/>
		</>
	);
};

export default OutstandingsDashboard;
